/**
 * Creating an account.
 *
 * The important behaviour is invisible: if you are already playing as a guest,
 * this **links** the email to the identity you already have rather than making
 * a second one. Your name, your avatar and anything you were entitled to come
 * with you. Getting that wrong strands all of it, and does so silently.
 *
 * It also says plainly that a lost password cannot be recovered from a new
 * device yet. That is true today, and a person choosing a password deserves to
 * know it while they are choosing.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerWithEmail } from '../net/auth.js';
import { useSession } from '../session-context.js';
import { describeError } from './errors.js';

const MIN_PASSWORD = 8;

export function Register() {
  const { session, adopt } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const ready = email.trim().length > 3 && password.length >= MIN_PASSWORD && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      // The existing session is passed in on purpose - see this file's header.
      await adopt(await registerWithEmail(email, password, session));
      navigate('/library');
    } catch (caught) {
      setError(
        describeError(caught, 'That address may already have an account. Try logging in.'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page narrow" onSubmit={submit}>
      <h1 className="page-title">Create an account</h1>
      <p className="field-help">
        You only need one to own games. Joining a friend&apos;s table never does.
      </p>

      <label className="field">
        <span className="field-label">Email</span>
        <input
          className="text-input"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          required
        />
      </label>

      <label className="field">
        <span className="field-label">Password</span>
        <input
          className="text-input"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
        />
        {tooShort ? (
          <span className="field-error">At least {MIN_PASSWORD} characters.</span>
        ) : null}
      </label>

      {/* Stated while they choose, not after they lose it. */}
      <p className="banner warn">
        Keep this password somewhere safe. We can send a reset link to your email, but we
        cannot recover an account whose address you no longer have.
      </p>

      {error ? <p className="banner error">{error}</p> : null}

      <button type="submit" className="big-button primary" disabled={!ready}>
        {busy ? 'Creating…' : 'Create account'}
      </button>

      <div className="page-links">
        <Link className="text-button" to="/login">
          I already have one
        </Link>
        <Link className="text-button" to="/">
          Back
        </Link>
      </div>
    </form>
  );
}
